// src/context/UserContext.tsx
import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from "react";
import { useConfig } from "./ConfigContext";
import { User, UserContextType } from "../types";
import { api } from "../services/api";
export const UserContext = createContext<UserContextType | undefined>(undefined);

export const UserProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [linkedAccounts, setLinkedAccounts] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const { getConfig, clearContext } = useConfig();

  // Load the user from the backend cookie session
  useEffect(() => {
    const loadUser = async () => {
      try {
        const res = await api.app.getCurrentUser();
        setUser(res.user);
        setLinkedAccounts(res.linkedAccounts ?? []);
      } catch (err) {
        console.error("Error loading user:", err);
        setUser(null);
        setLinkedAccounts([]);
      } finally {
        setLoading(false);
      }
    };
    loadUser();
  }, []);

  const refreshLinkedAccounts = useCallback(async () => {
    try {
      const res = await api.app.getCurrentUser();
      setLinkedAccounts(res.linkedAccounts ?? []);
    } catch (err) {
      console.error("Error refreshing linked accounts:", err);
    }
  }, []);

  // Login
  const login = async (user: any) => {

    try {
      await setUser(user); // backend should send { user } after login
      await getConfig();
      return Promise.resolve();
    } catch (err) {
      console.error("Login error:", err);
      throw err;
    }
  };

  // Logout
  const logout = async () => {
    try {
      await api.app.logout();
      await clearContext();
    } catch (err) {
      console.error("Logout error:", err);
    } finally {
      setUser(null);
      clearContext();
    }
  };

  return (
    <UserContext.Provider
      value={{
        user,
        loading,
        linkedAccounts,
        login,
        logout,
        setUser,
        refreshLinkedAccounts,
      }}
    >
      {children}
    </UserContext.Provider>
  );
};

export const useUser = () => {
  const ctx = useContext(UserContext);
  if (!ctx) throw new Error("useUser must be used within UserProvider");
  return ctx;
};
